import { BillingClosureBadges } from "../../components/BillingClosureBadges";
import { billingClosureState } from "../../utils/billingClosureState";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { GridToolbarContainer, GridToolbarQuickFilter, itIT } from "@mui/x-data-grid-pro";
import { PersistentDataGridPro as DataGridPro } from "../../components/DataGrid/PersistentDataGridPro";
import LinearProgress from "@mui/material/LinearProgress";
import { toast } from "react-toastify";
import api from "../../api/api";
import { CustomFooterPersonalized } from "../../components/DataGrid/CustomFooterPersonalized";
import Modal from "../../components/Modal";
import { Autocomplete } from "../../components/Autocomplete";
import { ForfaitConfigurationForm, forfaitMonthsFromRow } from "../../components/ForfaitConfigurationForm";
import { EXPORT_BUTTON_CLASS } from "../../utils/exportButtonStyle";
import { TotaliPrestazioniModal } from "../../components/TotaliPrestazioniModal";
import { IipayFasciaDetails } from "../../components/IipayFasciaDetails";
import { getForfaitFasciaDetails } from "../../utils/iipayFascia";
import { TotalSectionCard } from "../../components/TotalSectionCard";
import { formatItalianNumber } from "../../utils/formatItalianNumber";

const euro = (value) => `${formatItalianNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\u00a0€`;

const annoOf = (yyyyMm) => {
  if (!yyyyMm) return null;
  const anno = Number(String(yyyyMm).split("-")[0]);
  return Number.isFinite(anno) ? anno : null;
};

const MESI_BREVI = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const mesiLabel = (value) => String(value || "")
  .split(",")
  .map(Number)
  .filter((m) => m >= 1 && m <= 12)
  .map((m) => MESI_BREVI[m - 1])
  .join(", ") || "—";

const meseFatturazioneStoricoLabel = (mesiPrecedenti, mesiNuovi, periodicita) => {
  const precedenti = new Set(
    String(mesiPrecedenti || "").split(",").map(Number).filter((mese) => mese >= 1 && mese <= 12)
  );
  const mesi = String(mesiNuovi || "")
    .split(",")
    .map(Number)
    .filter((mese) => mese >= 1 && mese <= 12)
    .sort((a, b) => a - b);
  const aggiunti = mesi.filter((mese) => !precedenti.has(mese));
  if (aggiunti.length) return MESI_BREVI[aggiunti[0] - 1];
  if (mesi.length) return MESI_BREVI[mesi[0] - 1];
  const primoMeseStandard = { mensile: 1, trimestrale: 3, semestrale: 6, annuale: 12 }[periodicita];
  return primoMeseStandard ? MESI_BREVI[primoMeseStandard - 1] : "—";
};

export const Forfait = ({ initialMonth, livePeriodo, embedded = false, aziende = [], roleReadOnly = false }) => {
  const [mese, setMese] = useState(() => initialMonth || new Date().toISOString().slice(0, 7));
  const [righe, setRighe] = useState([]);
  const [periodo, setPeriodo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [nuovo, setNuovo] = useState(null);
  const [savingNuovo, setSavingNuovo] = useState(false);
  const [exportRange, setExportRange] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [clientiConfigurati, setClientiConfigurati] = useState([]);
  const [storicoGlobale, setStoricoGlobale] = useState(null);
  const [storicoGlobaleLoading, setStoricoGlobaleLoading] = useState(false);
  const [totaliOpen, setTotaliOpen] = useState(false);
  const [tipologieFiltro, setTipologieFiltro] = useState(new Set());

  const load = useCallback(async (signal = null) => {
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(mese)) return;
    const [anno, numeroMese] = mese.split("-").map(Number);
    setLoading(true);
    try {
      const { data } = await api.get("/billing/forfait", { params: { anno, mese: numeroMese }, signal });
      setRighe((data.righe || []).map((r) => ({ ...r, totale: Number(r.totale) })));
      setPeriodo(data.periodo || null);
      setClientiConfigurati(data.clienti_configurati || []);
    } catch (error) {
      if (signal?.aborted || error?.code === "ERR_CANCELED") return;
      toast.error(error.response?.data?.message || "Errore caricamento forfait");
    } finally {
      setLoading(false);
    }
  }, [mese]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const loadStoricoNuovo = async (forfaitId) => {
    if (!forfaitId) return;
    try {
      const { data } = await api.get(`/billing/forfait-config/${forfaitId}/storico`);
      setNuovo((v) => (v ? { ...v, storico: data } : v));
    } catch {
      toast.error("Errore caricamento storico");
    }
  };

  // Apre la modale unica di aggiunta/modifica: senza riga per un cliente nuovo,
  // con la riga della griglia quando si clicca su un cliente già configurato.
  const openNuovo = (row = null) => {
    if (row) {
      const importo = row.importo_configurato ?? "";
      const importo_forzato = row.gestionale === "iipay" ? false : Boolean(row.importo_forzato);
      const mesi = forfaitMonthsFromRow(row);
      const note = row.note || "";
      setNuovo({
        codice_univoco_azienda: row.codice_univoco_azienda,
        cliente: row.cliente,
        tipo: row.tipo === "B" ? "bpoint" : "consulenza",
        gestionale: row.gestionale,
        importo,
        importo_forzato,
        mesi,
        note,
        forfait_id: row.forfait_id,
        decorrenza: annoOf(row.decorrenza) || annoOf(mese),
        numero_cedolini: row.numero_cedolini,
        mese_considerato: row.mese_considerato,
        fascia: row.fascia,
        importo_rate_card: row.importo_rate_card,
        storico: null,
        cliente_locked: true,
        originale: { importo, importo_forzato, mesi, note },
      });
      loadStoricoNuovo(row.forfait_id);
      return;
    }
    setNuovo({
      codice_univoco_azienda: "",
      cliente: "",
      tipo: "consulenza",
      gestionale: "zucchetti",
      importo: "",
      importo_forzato: false,
      mesi: [],
      note: "",
      forfait_id: null,
      decorrenza: annoOf(mese),
      storico: null,
      cliente_locked: false,
    });
  };

  const handleSelectClienteNuovo = async (codice) => {
    const az = aziende.find((a) => String(a.codice_univoco) === String(codice));
    const rigaOperativa = righe.find((r) => String(r.codice_univoco_azienda) === String(codice));
    const isBpoint = az?.tipologia_fatturazione === "B";
    const base = {
      codice_univoco_azienda: codice,
      cliente: az?.ragione_sociale || "",
      tipo: isBpoint ? "bpoint" : "consulenza",
      gestionale: isBpoint ? "bpoint" : az?.tipologia_fatturazione === "I" ? "iipay" : "zucchetti",
      importo: "",
      importo_forzato: false,
      mesi: [],
      note: "",
      forfait_id: null,
      decorrenza: annoOf(mese),
      storico: null,
      cliente_locked: false,
      numero_cedolini: rigaOperativa?.numero_cedolini,
      mese_considerato: rigaOperativa?.mese_considerato,
      fascia: rigaOperativa?.fascia,
      importo_rate_card: rigaOperativa?.importo_rate_card,
    };
    setNuovo(base);
    if (base.gestionale === "iipay") {
      const [anno, numeroMese] = mese.split("-").map(Number);
      api.get(`/billing/forfait-iipay-context/${codice}`, { params: { anno, mese: numeroMese } })
        .then(({ data }) => setNuovo((current) => (
          current && String(current.codice_univoco_azienda) === String(codice)
            ? { ...current, ...data }
            : current
        )))
        .catch(() => toast.error("Errore caricamento fascia cedolini iiPay"));
    }
    if (!clientiConfigurati.includes(codice)) return;
    try {
      const { data } = await api.get(`/billing/forfait/${codice}`);
      const esistente = (data || []).find((f) => f.attivo && (f.tipo === "consulenza" || f.tipo === "bpoint"));
      if (!esistente) return;
      const importo = esistente.importo ?? "";
      const importo_forzato = (esistente.gestionale || base.gestionale) === "iipay"
        ? false
        : Boolean(esistente.importo_forzato);
      const mesi = forfaitMonthsFromRow(esistente);
      const note = esistente.note || "";
      setNuovo((current) => ({
        ...base,
        tipo: esistente.tipo,
        gestionale: esistente.gestionale || base.gestionale,
        importo,
        importo_forzato,
        mesi,
        note,
        forfait_id: esistente.id,
        decorrenza: annoOf(esistente.decorrenza_label) || annoOf(mese),
        numero_cedolini: current?.codice_univoco_azienda === codice ? current.numero_cedolini : rigaOperativa?.numero_cedolini,
        mese_considerato: current?.codice_univoco_azienda === codice ? current.mese_considerato : rigaOperativa?.mese_considerato,
        fascia: current?.codice_univoco_azienda === codice ? current.fascia : rigaOperativa?.fascia,
        importo_rate_card: current?.codice_univoco_azienda === codice ? current.importo_rate_card : rigaOperativa?.importo_rate_card,
        originale: { importo, importo_forzato, mesi, note },
      }));
      loadStoricoNuovo(esistente.id);
    } catch {
      toast.error("Errore caricamento configurazione cliente");
    }
  };

  const saveNuovo = async (annoOverride = null) => {
    if (!nuovo?.codice_univoco_azienda) { toast.error("Seleziona un cliente"); return; }
    if (!nuovo.mesi.length) { toast.error("Seleziona almeno un mese di fatturazione"); return; }
    const mesiOrdinati = [...nuovo.mesi].sort((a, b) => a - b);
    if (nuovo.forfait_id && nuovo.originale) {
      const invariato =
        Number(nuovo.importo || 0) === Number(nuovo.originale.importo || 0) &&
        Boolean(nuovo.importo_forzato) === Boolean(nuovo.originale.importo_forzato) &&
        mesiOrdinati.join(",") === [...nuovo.originale.mesi].sort((a, b) => a - b).join(",") &&
        (nuovo.note || "") === (nuovo.originale.note || "");
      if (invariato) {
        toast.warning("Nessuna variazione rispetto alla configurazione attuale: salvataggio annullato");
        return;
      }
    }
    const annoDecorrenza = annoOverride || Number(nuovo.decorrenza) || annoOf(mese);
    const decorrenzaSalvataggio = `${annoDecorrenza}-01`;
    setSavingNuovo(true);
    try {
      const periodicita = mesiOrdinati.length === 12 ? "mensile" : mesiOrdinati.length === 1 ? "annuale" : "custom";
      if (nuovo.forfait_id) {
        await api.put(`/billing/forfait-config/${nuovo.forfait_id}`, {
          importo: Number(nuovo.importo) || 0,
          mesi_fatturazione: mesiOrdinati.join(","),
          decorrenza: decorrenzaSalvataggio,
          note: nuovo.note,
          motivo: "",
          importo_forzato: nuovo.gestionale === "iipay" ? false : nuovo.importo_forzato,
        });
        toast.success(`Forfait aggiornato dall'anno ${annoDecorrenza}`);
      } else {
        const prestazione = nuovo.tipo === "bpoint" ? "590" : nuovo.gestionale === "iipay" ? "661" : "656";
        const descrizione = nuovo.gestionale === "iipay" ? "Professional assignment" : "Consulenze professionali come da contratto";
        await api.post("/billing/forfait", {
          codice_univoco_azienda: nuovo.codice_univoco_azienda,
          tipo: nuovo.tipo,
          gestionale: nuovo.gestionale,
          prestazione,
          importo: Number(nuovo.importo) || 0,
          periodicita,
          mesi_fatturazione: periodicita === "mensile" ? null : mesiOrdinati.join(","),
          decorrenza: decorrenzaSalvataggio,
          descrizione,
          note: nuovo.note || null,
          attivo: 1,
        });
        toast.success(`Cliente aggiunto dall'anno ${annoDecorrenza}`);
      }
      setNuovo(null);
      await load();
    } catch (error) {
      if (error.response?.status === 409) {
        const successivo = annoDecorrenza + 1;
        const conferma = window.confirm(
          `L'anno ${annoDecorrenza} risulta già chiuso/fatturato per questo cliente. Applicare la modifica dall'anno ${successivo}?`
        );
        setSavingNuovo(false);
        if (conferma) await saveNuovo(successivo);
        return;
      }
      toast.error(error.response?.data?.message || "Errore salvataggio forfait");
    } finally { setSavingNuovo(false); }
  };

  const handleDeleteStorico = async (row) => {
    if (!window.confirm(`Eliminare questa variazione e ripristinare i valori precedenti (importo ${euro(row.importo_precedente)})? L'operazione non può essere annullata.`)) return;
    try {
      await api.delete(`/billing/forfait-config/storico/${row.id}`);
      toast.success("Variazione annullata, valori precedenti ripristinati");
      setNuovo(null);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.message || "Errore eliminazione riga storico");
    }
  };

  const loadStoricoGlobale = async (anno = annoOf(mese)) => {
    setStoricoGlobale((current) => ({ anno, righe: current?.righe || [] }));
    setStoricoGlobaleLoading(true);
    try {
      const { data } = await api.get("/billing/forfait-config-storico-globale", { params: { anno } });
      setStoricoGlobale({ anno, righe: data || [] });
    } catch (error) {
      toast.error(error.response?.data?.message || "Errore caricamento storico globale");
    } finally {
      setStoricoGlobaleLoading(false);
    }
  };

  const openExportRange = () => {
    const annoCorrente = Number(mese.split("-")[0]);
    setExportRange({ da: annoCorrente, a: annoCorrente });
  };

  const handleExport = async () => {
    if (!exportRange) return;
    if (exportRange.da > exportRange.a) { toast.error("L'anno di inizio deve precedere quello di fine"); return; }
    setExporting(true);
    try {
      const res = await api.get("/billing/forfait/export-storico", {
        params: { anno_da: exportRange.da, anno_a: exportRange.a },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `forfait_storico_${exportRange.da}_${exportRange.a}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      setExportRange(null);
    } catch {
      toast.error("Errore esportazione storico forfait");
    } finally {
      setExporting(false);
    }
  };

  const columns = useMemo(() => [
    {
      field: "tipo",
      headerName: "Tipo",
      width: 65,
      type: "singleSelect",
      valueOptions: [
        { value: "Z", label: "Zucchetti" },
        { value: "I", label: "iiPay" },
        { value: "B", label: "Bpoint" },
      ],
      headerAlign: "center",
      align: "center",
      renderCell: ({ value }) => {
        const colors = {
          Z: "bg-blue-100 text-blue-700",
          I: "bg-green-100 text-green-700",
          B: "bg-purple-100 text-purple-700",
        };
        return <span className={`rounded px-1.5 py-0.5 text-xs font-bold ${colors[value] || "bg-gray-100 text-gray-600"}`}>{value}</span>;
      },
    },
    { field: "cliente", headerName: "Cliente", flex: 1, minWidth: 240 },
    { field: "cod", headerName: "Cod", width: 90 },
    { field: "prestazione", headerName: "Prestazione", flex: 1, minWidth: 280 },
    { field: "fascia", headerName: "Fascia iiPay", width: 300, align: "center", headerAlign: "center",
      renderCell: ({ row }) => <IipayFasciaDetails row={getForfaitFasciaDetails(row)} /> },
    { field: "totale", headerName: "Totale", width: 150, type: "number",
      renderCell: ({ value, row }) => (
        <strong
          title={row.importo_da_valutare ? `Cliente cessato il ${String(row.data_fine_elaborazioni).slice(0, 10)}: valutare se fatturare l'importo annuale` : ""}
          className={row.importo_da_valutare ? "rounded bg-amber-200 px-2 py-1 text-amber-900" : ""}
        >
          {euro(value)}
        </strong>
      )
    },
    { field: "settore", headerName: "Settore", width: 130 },
    { field: "note", headerName: "Note", flex: 1, minWidth: 180 },
  ], []);

  const storicoGlobaleColumns = useMemo(() => [
    { field: "cliente", headerName: "Cliente", flex: 1, minWidth: 240 },
    { field: "codice_parcellazione", headerName: "Codice", width: 105 },
    {
      field: "stato_cliente", headerName: "Stato cliente", width: 145,
      renderCell: ({ value }) => (
        <span className={`rounded px-2 py-0.5 text-xs font-semibold ${
          value === "Attivo" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-800"
        }`}>{value}</span>
      ),
    },
    { field: "decorrenza", headerName: "Decorrenza", width: 105 },
    { field: "importo_precedente", headerName: "Importo prima", width: 125, type: "number", renderCell: ({ value }) => euro(value) },
    { field: "importo_nuovo", headerName: "Importo dopo", width: 125, type: "number", renderCell: ({ value }) => <strong>{euro(value)}</strong> },
    {
      field: "mesi_nuovi",
      headerName: "Mese fatturazione",
      width: 175,
      renderCell: ({ row }) => <strong>{meseFatturazioneStoricoLabel(
        row.mesi_precedenti,
        row.mesi_nuovi,
        row.periodicita_nuova
      )}</strong>,
    },
    { field: "modificato_da", headerName: "Modificato da", width: 135 },
    { field: "motivo", headerName: "Motivo", minWidth: 180, flex: 0.7 },
    {
      field: "created_at", headerName: "Registrata il", width: 155,
      renderCell: ({ value }) => value ? new Date(value).toLocaleString("it-IT") : "—",
    },
  ], []);

  const totale = righe.reduce((sum, r) => sum + r.totale, 0);
  const righeVisibili = tipologieFiltro.size > 0
    ? righe.filter((row) => tipologieFiltro.has(String(row.tipo || "Z").toUpperCase()))
    : righe;
  const totaliPerTipologia = righe.reduce((result, row) => {
    const tipo = row.tipo || "Z";
    result[tipo] = (result[tipo] || 0) + Number(row.totale || 0);
    return result;
  }, { Z: 0, B: 0, I: 0 });
  const isEditMode = Boolean(nuovo?.forfait_id);
  const closure = billingClosureState(livePeriodo || periodo, 'forfait_consulenza');
  const selectedTipo = aziende.find(a => a.codice_univoco === nuovo?.codice_univoco_azienda)?.tipologia_fatturazione || (nuovo?.tipo === 'bpoint' ? 'B' : nuovo?.tipo);
  const readOnly = roleReadOnly || (nuovo ? (selectedTipo === 'B' ? closure.bpointClosed : closure.ziClosed) : closure.bpointClosed && closure.ziClosed);
  const Toolbar = () => (
    <GridToolbarContainer>
      <div className="flex flex-wrap items-center gap-2">
        <GridToolbarQuickFilter placeholder="Cerca cliente, tipo o codice..." />
        <div className="flex items-center gap-1">
          {[
            { id: "Z", active: "bg-blue-100 text-blue-700 border-blue-300", idle: "bg-white text-blue-700 border-blue-200 hover:bg-blue-50" },
            { id: "I", active: "bg-green-100 text-green-700 border-green-300", idle: "bg-white text-green-700 border-green-200 hover:bg-green-50" },
            { id: "B", active: "bg-purple-100 text-purple-700 border-purple-300", idle: "bg-white text-purple-700 border-purple-200 hover:bg-purple-50" },
          ].map(({ id, active, idle }) => (
            <button
              key={id}
              type="button"
              title={id === "Z" ? "Zucchetti" : id === "I" ? "iiPay" : "Bpoint"}
              onClick={() => setTipologieFiltro((current) => {
                const next = new Set(current);
                next.has(id) ? next.delete(id) : next.add(id);
                return next;
              })}
              className={`rounded border px-2.5 py-0.5 text-xs font-semibold transition-colors ${tipologieFiltro.has(id) ? active : idle}`}
            >
              {id}
            </button>
          ))}
        </div>
      </div>
    </GridToolbarContainer>
  );

  return (
    <div className={embedded ? "w-full" : "mx-auto w-full page-container gap-4"}>
      <div className="el-container p-4 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-800">Forfait</h1>
            <p className="text-sm text-gray-500">Importi da fatturare per competenza del mese precedente. Clicca un cliente in tabella per modificarlo.</p>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <BillingClosureBadges state={closure} />
            {!readOnly && (
              <button
                type="button"
                onClick={() => openNuovo()}
                className="rounded border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100"
              >
                Aggiungi/modifica cliente
              </button>
            )}
            <button
              type="button"
              onClick={() => loadStoricoGlobale()}
              className="rounded border border-purple-200 bg-purple-50 px-3 py-1.5 text-xs font-semibold text-purple-700 hover:bg-purple-100"
            >
              Storico globale
            </button>
            <button
              type="button"
              onClick={openExportRange}
              className={EXPORT_BUTTON_CLASS}
            >
              Esporta Excel
            </button>
            {!embedded && (
              <input
                type="month"
                value={mese}
                onChange={(event) => setMese(event.target.value)}
                className="text-sm"
              />
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-5 text-sm text-gray-600">
          <span><strong>{righe.length}</strong> clienti con importo da fatturare</span>
          {periodo && <span>Periodo: <strong>{String(periodo.mese).padStart(2, "0")}/{periodo.anno}</strong></span>}
          {righe.some((r) => r.importo_da_valutare) && (
            <span className="rounded bg-amber-100 px-2 py-1 text-amber-800">
              Gli importi in ambra sono di clienti cessati nel periodo coperto: valutare se fatturarli.
            </span>
          )}
        </div>

        <TotalSectionCard total={totale} totalsByType={totaliPerTipologia} onClick={() => setTotaliOpen(true)} />

        <div style={{ height: "calc(100vh - 140px)", minHeight: 620 }}>
          <DataGridPro
            density="compact"
            rows={righeVisibili}
            columns={columns}
            getRowId={(row) => row.id}
            loading={loading}
            localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
            slots={{ toolbar: Toolbar, footer: CustomFooterPersonalized, loadingOverlay: LinearProgress }}
            disableRowSelectionOnClick
            onRowClick={(params) => { if (!roleReadOnly && !(params.row.tipo === 'B' ? closure.bpointClosed : closure.ziClosed)) openNuovo(params.row); }}
            getRowClassName={({ row }) => `cursor-pointer ${row.importo_da_valutare ? "bg-amber-50" : ""}`}
          />
        </div>

        <TotaliPrestazioniModal
          isOpen={totaliOpen}
          onClose={() => setTotaliOpen(false)}
          subtitle={`Forfait ${mese.slice(5, 7)}/${mese.slice(0, 4)}`}
          rows={righe.map((row) => ({
            tipo: row.tipo,
            codice: row.cod,
            descrizione: row.prestazione,
            quantita: 1,
            totale: row.totale,
          }))}
        />

        {nuovo && (
          <Modal isOpen size="compact" closeButton={() => setNuovo(null)} headerText={isEditMode ? `Forfait — ${nuovo.cliente}` : "Aggiungi cliente al forfait"}>
            <div className="space-y-3 p-3 text-sm">
              <Autocomplete
                label="Cliente"
                placeholder="Cerca cliente..."
                value={nuovo.codice_univoco_azienda}
                onChange={(e) => handleSelectClienteNuovo(e.target.value)}
                disabled={nuovo.cliente_locked}
                options={aziende.map((a) => ({
                  id: a.codice_univoco,
                  name: `${a.ragione_sociale}${clientiConfigurati.includes(a.codice_univoco) ? " (configurato)" : ""}${a.data_fine_elaborazioni ? " (cessato)" : ""}`,
                  badge: a.tipologia_fatturazione,
                }))}
              />
              {nuovo.codice_univoco_azienda && (
                <>
                  <ForfaitConfigurationForm
                    value={{
                      ...nuovo,
                      importoBloccato: nuovo.gestionale === "iipay",
                    }}
                    onChange={({ importoBloccato, ...nextValue }) => setNuovo(nextValue)}
                    disabled={readOnly}
                    showHistory={isEditMode}
                    onDeleteHistory={handleDeleteStorico}
                    rateCard={nuovo.gestionale === "iipay" && (nuovo.numero_cedolini != null || nuovo.fascia) ? {
                      numeroCedolini: nuovo.numero_cedolini,
                      fascia: nuovo.fascia,
                      importo: nuovo.importo_rate_card,
                      meseConsiderato: nuovo.mese_considerato,
                    } : null}
                  />

                  <div className="flex gap-2 pt-1">
                    {!readOnly && (
                      <button onClick={() => saveNuovo()} disabled={savingNuovo} className="rounded bg-blue-600 px-3 py-1.5 text-white disabled:opacity-50">{savingNuovo ? "Salvataggio..." : "Salva"}</button>
                    )}
                    <button onClick={() => setNuovo(null)} className="rounded border px-3 py-1.5">{readOnly ? "Chiudi" : "Annulla"}</button>
                  </div>
                </>
              )}
            </div>
          </Modal>
        )}

        {exportRange && (
          <Modal isOpen size="small" closeButton={() => setExportRange(null)} headerText="Esporta storico forfait">
            <div className="space-y-4 p-4">
              <p className="text-sm text-gray-500">Un foglio Excel per ogni anno dell'intervallo, con gli importi fatturati mese per mese.</p>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-sm">Da (anno)
                  <input type="number" value={exportRange.da}
                    onChange={(e) => setExportRange((v) => ({ ...v, da: Number(e.target.value) }))}
                    className="mt-1 w-full rounded border px-2 py-1.5" />
                </label>
                <label className="text-sm">A (anno)
                  <input type="number" value={exportRange.a}
                    onChange={(e) => setExportRange((v) => ({ ...v, a: Number(e.target.value) }))}
                    className="mt-1 w-full rounded border px-2 py-1.5" />
                </label>
              </div>
              <div className="flex gap-2">
                <button onClick={handleExport} disabled={exporting} className={EXPORT_BUTTON_CLASS}>{exporting ? "Esportazione..." : "Esporta Excel"}</button>
                <button onClick={() => setExportRange(null)} className="rounded border px-4 py-2 text-sm">Annulla</button>
              </div>
            </div>
          </Modal>
        )}

        {storicoGlobale && (
          <Modal
            isOpen
            size="large"
            fullHeight
            closeButton={() => setStoricoGlobale(null)}
            headerText="Storico globale modifiche forfait"
          >
            <div className="flex h-full min-h-0 flex-col p-2">
              <div className="min-h-[480px] flex-1">
                <DataGridPro
                  density="compact"
                  rows={storicoGlobale.righe}
                  columns={storicoGlobaleColumns}
                  getRowId={(row) => row.id}
                  loading={storicoGlobaleLoading}
                  localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
                  slots={{ toolbar: Toolbar, footer: CustomFooterPersonalized, loadingOverlay: LinearProgress }}
                  disableRowSelectionOnClick
                />
              </div>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default Forfait;
